import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import {
    Header,
    HStack,
    type HVStackProps,
    IconArrowLeftBig,
    IconCard,
    IconGeoTag,
    IconHistory,
    Label,
    Text,
    VStack,
} from '@direct-frontend/components';
import type { Nullable, Optional } from '@direct-frontend/stdlib';
import { getGeoCoderAddress } from '@/entities/geoCoder';
import { checkOrderDeliveryAvailable } from '@/entities/order';
import { trackGoal } from '@/entities/trackGoal';
import { type MapSubmitButtonComponent } from '@/shared/api/map';
import { OrderDeliveryServices, type OrderDto } from '@/shared/api/order';
import { type PickupPoint, PickupPointPaymentMethods } from '@/shared/api/pickupPoint';
import { Sticky } from '@/shared/components/Sticky';
import { useMountedState } from '@/shared/lib/hooks';
import { pluralize } from '@/shared/lib/pluralize';
import TimeIcon from './assets/Time.react.svg';
import { formatPaymentMethod, formatSchedule } from '../../lib';
import type { PickupAddressValue } from '../../types/PickupAddressValue';
import styles from './PickupAddressDetails.module.css';

const ERROR_MESSAGES = {
    UNSUPPORTED_ADDRESS: 'Не можем доставить по этому адресу',
};

interface UsePickupAddressControllerProps {
    order: OrderDto;
    storeSlug: string;
    onChange?: (values: PickupAddressValue) => void;
    onClose?: VoidFunction;
    reinitializeOrderForm: (order: OrderDto, updatedOrder: OrderDto) => void;
}

interface UsePickupAddressControllerState {
    submitting: boolean;
    errorCode: Nullable<'UNSUPPORTED_ADDRESS'>;
}

interface UsePickupAddressControllerPayload {
    state: UsePickupAddressControllerState;
    setState: Dispatch<SetStateAction<UsePickupAddressControllerState>>;
    onChange: (point: PickupPoint) => Promise<void>;
}

function usePickupAddressController(props: UsePickupAddressControllerProps): UsePickupAddressControllerPayload {
    const { onChange } = props;
    const isMounted = useMountedState();
    const [state, setState] = useState<UsePickupAddressControllerState>({ errorCode: null, submitting: false });

    const handleChange = useCallback(
        async (point: PickupPoint) => {
            setState((currentState) => {
                return { ...currentState, submitting: true };
            });

            try {
                const { locality, address } = await getGeoCoderAddress({ coordinates: point.position });

                if (point.serviceType === OrderDeliveryServices.SELF) {
                    if (!address) {
                        setState((currentState) => {
                            return {
                                ...currentState,
                                errorCode: 'UNSUPPORTED_ADDRESS',
                                submitting: false,
                            };
                        });

                        return;
                    }

                    setState((currentState) => {
                        return { ...currentState, submitting: false };
                    });

                    onChange?.({
                        pickupPointId: point.id,
                        serviceType: point.serviceType,
                        address,
                        locality: locality || '',
                    });
                } else {
                    const { is_available } = await checkOrderDeliveryAvailable({
                        pickup_point_id: point.id,
                        pickup_point_service_type: point.serviceType,
                    });

                    if (!is_available) {
                        setState((currentState) => {
                            return { ...currentState, errorCode: 'UNSUPPORTED_ADDRESS', submitting: false };
                        });

                        return;
                    }

                    if (isMounted()) {
                        if (!address) {
                            setState((currentState) => {
                                return {
                                    ...currentState,
                                    errorCode: 'UNSUPPORTED_ADDRESS',
                                    submitting: false,
                                };
                            });

                            return;
                        }

                        setState((currentState) => {
                            return { ...currentState, submitting: false };
                        });
                        onChange?.({
                            pickupPointId: point.id,
                            serviceType: point.serviceType,
                            address,
                            locality: locality || '',
                        });
                    }
                }
            } catch (e) {
                if (isMounted()) {
                    setState((currentState) => {
                        return {
                            ...currentState,
                            errorCode: 'UNSUPPORTED_ADDRESS',
                            submitting: false,
                        };
                    });
                }
            }
        },
        [onChange, isMounted]
    );

    return {
        state,
        setState,
        onChange: handleChange,
    };
}

type PickupAddressDetailsProps = HVStackProps &
    UsePickupAddressControllerProps & {
        point: PickupPoint;
        allItemsCount: Optional<number>;
        submitButton: MapSubmitButtonComponent;
    };

export const PickupAddressDetails = (props: PickupAddressDetailsProps) => {
    const { point, order, storeSlug, allItemsCount, onClose, submitButton: SubmitButton, ...other } = props;

    const ref = useRef<HTMLButtonElement>(null);
    const { state, setState, onChange } = usePickupAddressController(props);

    const onPointSelect = useCallback(
        (callback?: VoidFunction) => {
            onChange?.(point);
            //TODO SS-3299 Если избавимся от хардкора времени хранения заказа добавить поле collection_period
            trackGoal('pickup_point_selected', {
                cash_available: point.paymentMethods.includes(PickupPointPaymentMethods.CASH_ON_RECEIPT),
                card_prepaid_available: point.paymentMethods.includes(PickupPointPaymentMethods.ALREADY_PAID),
                card_postpaid_available: point.paymentMethods.includes(PickupPointPaymentMethods.CARD_ON_RECEIPT),
                working_times: formatSchedule(point.schedule).join(' '),
            });

            callback?.();
        },
        [point, onChange]
    );

    useEffect(() => {
        setState((currentState) => {
            return { ...currentState, errorCode: null };
        });
    }, [point, setState]);

    const renderItemsCount = useCallback(() => {
        if (!allItemsCount || !point.itemsCount || (point.itemsCount === 1 && allItemsCount === 1)) {
            return null;
        }

        const itemsPluralize = pluralize(allItemsCount, '%d товара', '%d товаров', '%d товаров');

        return (
            <Label>
                Можно забрать {point.itemsCount} из {itemsPluralize}
            </Label>
        );
    }, [allItemsCount, point.itemsCount]);

    return (
        <VStack gap="24" justifyContent="space-between" {...other}>
            <VStack gap="24">
                <VStack gap="24">
                    <IconArrowLeftBig size="24" className={styles.close} onClick={onClose} />
                    <Header level="h2">{point.name}</Header>
                    <VStack gap="16">
                        <HStack gap="16" alignItems="center">
                            <IconGeoTag size="24" />
                            <Text>{point.address}</Text>
                        </HStack>
                        <HStack gap="16" alignItems="center">
                            <IconHistory size="24" />
                            <HStack gap="4" flexWrap="wrap">
                                {formatSchedule(point.schedule).map((schedule, index) => (
                                    <Text key={index} className={styles.nowrap}>
                                        {schedule}
                                    </Text>
                                ))}
                            </HStack>
                        </HStack>
                        <HStack gap="16" alignItems="center">
                            <TimeIcon width="24" />
                            <Text>Срок хранения 7 дней</Text>
                        </HStack>
                        <HStack gap="16" alignItems="center">
                            <IconCard size="24" />
                            <Text>{formatPaymentMethod(point.paymentMethods).join(', ')}</Text>
                        </HStack>
                    </VStack>
                </VStack>
                {point.comment && (
                    <>
                        <div className={styles.divider} />

                        <VStack gap="8">
                            <Header level="h3">Как добраться</Header>
                            <Text>{point.comment}</Text>
                        </VStack>
                    </>
                )}

                {renderItemsCount()}
            </VStack>
            <VStack gap="8">
                {state.errorCode && (
                    <Text color="red" size="xs">
                        {ERROR_MESSAGES[state.errorCode] ?? state.errorCode}
                    </Text>
                )}

                <SubmitButton ref={ref} point={point} onSubmit={onPointSelect} isSubmitting={state.submitting} />

                <Sticky threshold={0.01} target={ref}>
                    {({ isSticky, stickStyle, style }) => (
                        <div className={cn(isSticky ? stickStyle : style, styles.stickyFooter)}>
                            <SubmitButton point={point} onSubmit={onPointSelect} isSubmitting={state.submitting} />
                        </div>
                    )}
                </Sticky>
            </VStack>
        </VStack>
    );
};
